import { RadioGroup as RadioPrimitive } from 'radix-ui'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { STATUSES, statusMeta, statusSelectedClass } from '../status'
import type { AttendanceStatus } from '../types'

interface StatusSegmentedProps {
  value: AttendanceStatus | null
  onChange: (status: AttendanceStatus) => void
  /** Accessible name, e.g. "Attendance for Ada Lovelace". */
  label: string
  disabled?: boolean
}

/**
 * Large segmented status picker with radio-group semantics (arrow keys move between options,
 * one tab stop per student). Letters on phones, full labels from `sm` up.
 */
export function StatusSegmented({ value, onChange, label, disabled }: StatusSegmentedProps) {
  const { t } = useTranslation('attendance')
  return (
    <RadioPrimitive.Root
      value={value ?? ''}
      onValueChange={(v) => onChange(v as AttendanceStatus)}
      orientation="horizontal"
      aria-label={label}
      disabled={disabled}
      className="inline-flex w-full rounded-control border border-line bg-surface-sunken p-0.5 sm:w-auto"
    >
      {STATUSES.map((s) => (
        <RadioPrimitive.Item
          key={s}
          value={s}
          aria-label={t(statusMeta[s].label)}
          title={t(statusMeta[s].label)}
          className={cn(
            'flex h-10 min-w-10 flex-1 items-center justify-center rounded-xs border border-transparent px-2 text-sm font-semibold text-ink-soft transition-colors hover:bg-surface hover:text-ink disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:px-3',
            statusSelectedClass[s],
          )}
        >
          <span className="sm:hidden" aria-hidden="true">
            {t(statusMeta[s].short)}
          </span>
          <span className="hidden sm:inline" aria-hidden="true">
            {t(statusMeta[s].label)}
          </span>
        </RadioPrimitive.Item>
      ))}
    </RadioPrimitive.Root>
  )
}
